/* ============================================================================
   LOOMISH SHOP — collection + product + cart
   ----------------------------------------------------------------------------
   THE ROOMS AND THE LENS: every family its own room; one piece in the middle
   of a white room, its neighbours quietly faded. On a desk the page glides on
   a two-stage follow and lands on a piece; on touch the device's own scroll
   and snapping. The stylesheet draws the fall-off on the compositor. This
   file reads the hand, keeps the room, and answers WHICH piece is in focus.

   Cart uses Shopify's own AJAX endpoints (/cart/add.js, /cart.js,
   /cart/change.js). Nothing about products, variants, inventory or checkout is
   reimplemented — it is the real cart, wearing this interface.
   ========================================================================== */
(function () {
  'use strict';

  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
   * The same motion scale the stylesheet declares. A large object crossing the
   * screen is the one thing allowed to take longer than a panel — it has real
   * distance to cover, and rushing it is what makes a transition look cheap.
   */
  var MOVE = 'cubic-bezier(0.62, 0, 0.25, 1)';
  var T_SLOW = 620;

  var num = function (v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; };
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };
  /* Smoothstep. The reason the focus fall-off has no visible edge. */
  var smooth = function (t) { return t * t * (3 - 2 * t); };

  /*
   * The stylesheet's --ls-in curve, solved in JavaScript.
   *
   * The pager moves the page with a script, so its easing cannot come from
   * CSS — and a hand-rolled approximation would be a second, slightly
   * different motion language running beside the first. This evaluates the
   * exact same cubic-bezier, so a piece arriving under the lens decelerates
   * on the identical curve as a panel arriving from off-screen.
   */
  function bezier(x1, y1, x2, y2) {
    var A = function (a, b) { return 1 - 3 * b + 3 * a; };
    var B = function (a, b) { return 3 * b - 6 * a; };
    var C = function (a) { return 3 * a; };
    var calc = function (t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; };
    var slope = function (t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); };

    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      /* Newton-Raphson: four passes is well past visually exact here. */
      var t = x;
      for (var i = 0; i < 4; i++) {
        var d = slope(t, x1, x2);
        if (d === 0) break;
        t -= (calc(t, x1, x2) - x) / d;
      }
      return calc(t, y1, y2);
    };
  }

  /* --ls-in: entering, decelerating. */
  var EASE_IN = bezier(0.16, 1, 0.3, 1);

  /*
   * Put the page at y, now.
   *
   * The theme's base.css sets `scroll-behavior: smooth` on the whole page,
   * and a bare scrollTo obeys it - so every frame of the pager's glide asked
   * the browser to begin a fresh smooth scroll of its own toward a target
   * that moved again a frame later. The glide dragged, stalled and arrived
   * late, and a cut slid across forty pieces instead of cutting. `instant`
   * is the one value the page's setting cannot override.
   */
  function jump(y) {
    try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, y); }
  }

  function money(cents, currency) {
    var v = (cents || 0) / 100;
    try {
      var s = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency || 'USD'
      }).format(v);
      return s.replace(/[.,]00(?=\D*$)/, '');
    } catch (e) {
      return '$' + v.toFixed(2).replace(/\.00$/, '');
    }
  }

  /* ============================================================== THE LENS */

  /*
   * v4 — 27 Sept. Rooms, and a page that moves like it was made.
   *
   * THE ROOMS. Every family is its own room. The room is an attribute on the
   * page (data-room); the stylesheet hides everything from other families, and
   * this file keeps a list of the pieces that are left - `slides` - numbered
   * from one. Nothing below ever counts past the room it is in.
   *
   * THE PHYSICS, on a desk (Glide, below). Taken from the gallery the founder
   * pointed at - its feel was never the effect, it was this:
   *
   *   hand -> target -> relay -> page
   *
   * The wheel moves a target. A relay chases the target, the page chases the
   * relay, each by the same frame-rate-independent step: 1 - (1 - k)^(dt*60).
   * Two chasers in a row start and stop on an S-curve, which is the whole
   * difference between gliding and lurching. When the hand stops - after a
   * pause scaled to the visitor's own rhythm - the target settles on a piece;
   * near the end the pull tightens, and the last twentieth of a pixel is
   * exact. The page's own scroll is what moves, so the header, the footer,
   * back, find-in-page and the scrollbar all keep working.
   *
   * Touch keeps the device's own scrolling and snapping - on a phone that is
   * already the best motion there is, and imitating it is how sites break it.
   *
   * WHICH piece is in the middle is still asked of an IntersectionObserver on
   * a line across the middle of the glass: no layout reads while moving.
   */
  var TIMELINE = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
  var FINE = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  var slice = function (l) { return Array.prototype.slice.call(l || []); };
  var pad = function (n) { return n < 10 ? '0' + n : String(n); };

  function Stream(root) {
    var self = this;
    var d = root.dataset;

    this.root = root;
    root.__ls = this;
    this.el = root.querySelector('.ls-stream');
    if (!this.el) return;
    this.all = slice(root.querySelectorAll('.ls-slide'));
    if (!this.all.length) return;
    this.slides = [];
    this.endEl = root.querySelector('[data-ls-end]');
    this.pos = -1;
    this.atEnd = false;
    this.cap = root.querySelector('[data-ls-caption]');
    this.capBody = root.querySelector('[data-ls-caption-body]') || this.cap;
    this.at = root.querySelector('[data-ls-at]');
    this.of = root.querySelector('[data-ls-of]');
    this.prefix = d.prefix || 'Nº';
    this.fade = num(d.fade, 22) / 100;
    this.shrink = num(d.shrink, 90) / 100;
    this.stale = true;

    this.warp = new Warp(root, this);
    this.room();

    if ('IntersectionObserver' in window) {
      /* The numbers, the label and the room bar's line belong to the stream. */
      this.band = new IntersectionObserver(function (en) {
        root.classList.toggle('is-streaming', en[0].isIntersecting);
      }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
      this.band.observe(this.el);

      /* The piece lying across the middle of the glass is the piece in focus. */
      this.mid = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          if (en.target === self.endEl) { self.focusEnd(); return; }
          var p = self.slides.indexOf(en.target);
          if (p >= 0 && (p !== self.pos || self.atEnd)) self.focus(p);
        });
      }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });

      /* Pieces near the glass are fetched before they are needed - and they
         are the only ones the glide ever touches while moving. */
      this.nearSet = [];
      this.near = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          en.target.classList.toggle('is-near', en.isIntersecting);
          var k = self.nearSet.indexOf(en.target);
          if (en.isIntersecting) {
            self.warm(en.target);
            if (k < 0) self.nearSet.push(en.target);
            if (self.breathNow && self.breathNow !== '1') {
              var fr = en.target.querySelector('.ls-slide__frame');
              if (fr) fr.style.scale = self.breathNow;
            }
          } else if (k >= 0) {
            self.nearSet.splice(k, 1);
            var fo = en.target.querySelector('.ls-slide__frame');
            if (fo) fo.style.scale = '';
          }
        });
        if (!TIMELINE) self.request();
      }, { rootMargin: '120% 0px 120% 0px', threshold: 0 });

      this.all.forEach(function (s) { self.mid.observe(s); self.near.observe(s); });
      if (this.endEl) this.mid.observe(this.endEl);
    } else {
      root.classList.add('is-streaming');
    }

    this.focus(0, true);

    /* Where the page may rest is measured once, and again only when the page
       itself changes shape. */
    var stale = function () { self.stale = true; };
    this.onResize = function () {
      self.stale = true;
      /* A desk resized while resting on a piece keeps that piece in the
         middle rather than drifting off it. */
      if (self.glide && !self.glide.raf && self.glide.aligned && !self.atEnd && self.inZone()) {
        clearTimeout(self.rsT);
        self.rsT = setTimeout(function () {
          var to = self.anchor(self.pos);
          if (to !== null && Math.abs(to - window.scrollY) > 2) { jump(to); self.glide.sync(); }
        }, 120);
      }
    };
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', stale);
    root.addEventListener('ls:mode', stale);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(stale);
    if ('ResizeObserver' in window) { this.ro = new ResizeObserver(stale); this.ro.observe(this.el); }
    if (FINE && d.snap === '1' && !RM.matches) this.glide = new Glide(this);

    /*
     * Coming back to the page (the back button, a restored tab), the browser
     * puts the scroll where it was before the page has finished settling, and
     * it lands a little off the piece. Once it is still, the page is seated on
     * the nearest piece - instantly, no glide, nothing to watch.
     */
    this.reseat = function () {
      if (!self.glide || self.glide.raf || self.root.dataset.mode !== 'immersive') return;
      self.stale = true;
      if (!self.inZone()) { self.glide.sync(); return; }
      var P = self.points();
      var k = self.nearest(window.scrollY);
      if (k >= 0 && Math.abs(P[k] - window.scrollY) > 2) jump(P[k]);
      self.glide.sync();
    };
    window.addEventListener('pageshow', function (e) {
      self.stale = true;
      if (e.persisted) setTimeout(self.reseat, 60);
    });
    window.addEventListener('load', function () { setTimeout(self.reseat, 80); });

    /*
     * A piece that is not in focus is brought forward by a click, never
     * opened by accident; the piece in focus opens. On a phone the label
     * under each rug is part of the same door.
     */
    this.el.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      var slide = e.target.closest('.ls-slide');
      if (!slide) return;
      var p = self.slides.indexOf(slide);
      if (p < 0) return;
      var onFrame = e.target.closest('.ls-slide__frame');
      var onLabel = e.target.closest('.ls-slide__meta');
      if (!onFrame && !onLabel) return;
      if (p !== self.pos || self.atEnd) { e.preventDefault(); self.centre(p); return; }
      if (onLabel) {
        var f = slide.querySelector('.ls-slide__frame');
        if (f) window.location.href = f.getAttribute('href');
      }
    });

    if (!TIMELINE && !RM.matches) {
      this.onScroll = function () { self.request(); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.request();
    }
  }

  /*
   * The room changed (or the page arrived in one): which pieces are here, and
   * their numbers in this room. The numbers in the label are rewritten, so a
   * room of ten reads Nº 01 to Nº 10, not Nº 34 to Nº 43.
   */
  Stream.prototype.room = function () {
    var key = this.root.dataset.room || 'all';
    var pre = this.prefix;
    /* The piece in focus belonged to the last room; nothing keeps its mark. */
    this.all.forEach(function (s) { s.classList.remove('is-focus'); });
    this.slides = this.all.filter(function (s) { return key === 'all' || s.dataset.room === key; });
    this.slides.forEach(function (s, k) {
      var no = s.querySelector('.ls-slide__no');
      if (no) no.textContent = pre + ' ' + pad(k + 1);
    });
    var cards = slice(this.root.querySelectorAll('.ls-card')).filter(function (c) { return key === 'all' || c.dataset.room === key; });
    cards.forEach(function (c, k) {
      var no = c.querySelector('.ls-card__no');
      if (no) no.textContent = pad(k + 1);
    });
    if (this.of) this.of.textContent = pad(this.slides.length);
    if (this.warp) this.warp.rebuild(key);
    this.stale = true;
    this.pos = -1;
    this.atEnd = false;
  };

  /* The glass that is hidden behind the header and the room bar. */
  Stream.prototype.covered = function () {
    var cs = getComputedStyle(this.root);
    return num(cs.getPropertyValue('--ls-header-h'), 0) + num(cs.getPropertyValue('--ls-bar-h'), 0);
  };

  /*
   * Every place the page may rest inside the room: the middle of each piece,
   * and the end card. Pieces have fixed heights, so this is measured once and
   * cached until the page changes shape.
   */
  Stream.prototype.points = function () {
    if (this.pts && !this.stale) return this.pts;
    var sy = window.scrollY;
    var vh = window.innerHeight;
    var mid = (vh + this.covered()) / 2;
    var max = Math.max(0, document.documentElement.scrollHeight - vh);
    var pts = [];
    for (var i = 0; i < this.slides.length; i++) {
      var r = this.slides[i].getBoundingClientRect();
      pts.push(clamp(sy + r.top + r.height / 2 - mid, 0, max));
    }
    if (this.endEl && this.endEl.offsetParent) {
      var e = this.endEl.getBoundingClientRect();
      pts.push(clamp(sy + e.top + e.height / 2 - mid, 0, max));
    }
    this.pts = pts;
    this.max = max;
    this.zone = pts.length ? [pts[0] - vh * 0.5, pts[pts.length - 1] + vh * 0.5] : [0, 0];
    this.stale = false;
    return pts;
  };

  Stream.prototype.maxScroll = function () {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  };

  Stream.prototype.inZone = function (y) {
    this.points();
    if (y === undefined) y = window.scrollY;
    return y >= this.zone[0] && y <= this.zone[1];
  };

  /* The resting place nearest y, and its index, or -1. */
  Stream.prototype.nearest = function (y) {
    var P = this.points(), k = -1, best = Infinity;
    for (var i = 0; i < P.length; i++) {
      var dd = Math.abs(P[i] - y);
      if (dd < best) { best = dd; k = i; }
    }
    return k;
  };

  /*
   * Where a gesture lands. The nearest piece to where the hand was sending the
   * page - except that a push which would land back where it began goes one
   * piece on, so a single notch of a mouse is always one piece, and a flick
   * lands wherever it was really going. Past the first piece going up, or the
   * end card going down, nothing is caught: the heading and the footer scroll
   * freely.
   */
  Stream.prototype.land = function (target, from) {
    var P = this.points();
    if (!P.length || target < this.zone[0] || target > this.zone[1]) return null;
    var k = this.nearest(target);
    var kf = this.nearest(from);
    var dir = target > from ? 1 : -1;
    if (kf >= 0 && Math.abs(from - P[kf]) < 3 && k === kf && Math.abs(target - from) > 36) k = kf + dir;
    if (k < 0 || k >= P.length) return null;
    return P[k];
  };

  /* The next resting place in a direction, for the keyboard. */
  Stream.prototype.step = function (dir, base) {
    var P = this.points();
    if (!P.length) return null;
    /* From the entrance, the first step down is the first piece. */
    if (dir > 0 && base < P[0] - 2 && base >= P[0] - window.innerHeight * 1.2) return P[0];
    if (!this.inZone(base)) return null;
    var kf = this.nearest(base);
    var k;
    if (kf >= 0 && Math.abs(P[kf] - base) < 3) k = kf + dir;
    else if (dir > 0) { for (k = 0; k < P.length && P[k] <= base + 2; k++); }
    else { for (k = P.length - 1; k >= 0 && P[k] >= base - 2; k--); }
    if (k < 0 || k >= P.length) return null;
    return P[k];
  };

  /* Where the page must stand for piece `p` of the room to be in the middle. */
  Stream.prototype.anchor = function (p) {
    var s = this.slides[p];
    if (!s) return null;
    var r = s.getBoundingClientRect();
    var to = window.scrollY + r.top + r.height / 2 - (window.innerHeight + this.covered()) / 2;
    return clamp(to, 0, this.maxScroll());
  };

  /* The next piece is fetched before it is needed. */
  Stream.prototype.warm = function (slide) {
    var img = slide && slide.querySelector('.ls-slide__img');
    if (!img || img.dataset.warm === '1') return;
    img.dataset.warm = '1';
    if (img.loading === 'lazy') img.loading = 'eager';
  };

  Stream.prototype.request = function () {
    if (TIMELINE || this.queued) return;
    this.queued = true;
    var self = this;
    requestAnimationFrame(function () { self.queued = false; self.draw(); });
  };

  /* Fallback only: the same fall-off as the stylesheet's scroll-driven one. */
  Stream.prototype.draw = function () {
    if (TIMELINE) return;
    var vh = window.innerHeight;
    var mid = (vh + this.covered()) / 2;
    var self = this;
    this.slides.forEach(function (s, i) {
      if (!s.classList.contains('is-near')) return;
      var f = s.querySelector('.ls-slide__frame');
      if (!f) return;
      var r = s.getBoundingClientRect();
      var c = r.top + r.height / 2;
      var t = clamp(Math.abs(c - mid) / (vh * 0.62), 0, 1);
      if (i === 0 && c > mid) t = 0;
      var e = smooth(t);
      f.style.opacity = (1 - e * (1 - self.fade)).toFixed(3);
      f.style.transform = 'scale(' + (1 - e * (1 - self.shrink)).toFixed(4) + ')';
    });
  };

  /*
   * The label is one fixed block the pieces pass behind, the way a gallery
   * keeps its wall text in one place. It lets go, takes the new piece's words
   * and resolves again - nothing slides or reflows - and carries that piece's
   * own href, taken from the frame.
   */
  Stream.prototype.focus = function (p, quiet) {
    var self = this;
    var prev = this.slides[this.pos];
    if (prev) prev.classList.remove('is-focus');
    this.pos = p;
    this.atEnd = false;
    this.root.classList.remove('is-at-end');
    var s = this.slides[p];
    if (s) s.classList.add('is-focus');
    if (this.warp) this.warp.set(p);
    if (this.at) this.at.textContent = pad(p + 1);

    if (this.cap && s) {
      var meta = s.querySelector('.ls-slide__meta');
      var frame = s.querySelector('.ls-slide__frame');
      if (meta) {
        this.cap.classList.remove('is-on');
        clearTimeout(this.capT);
        this.capT = setTimeout(function () {
          self.capBody.innerHTML = meta.innerHTML;
          if (frame) self.cap.setAttribute('href', frame.getAttribute('href'));
          self.cap.classList.add('is-on');
        }, quiet || RM.matches ? 0 : 160);
      }
    }
    this.root.dispatchEvent(new CustomEvent('ls:focus', { detail: { index: p } }));
  };

  /* The end card is in the middle: the label steps away, the count stays. */
  Stream.prototype.focusEnd = function () {
    var prev = this.slides[this.pos];
    if (prev) prev.classList.remove('is-focus');
    this.atEnd = true;
    this.root.classList.add('is-at-end');
    if (this.cap) this.cap.classList.remove('is-on');
  };

  /*
   * Carry the page to a piece of the room. Near: glided there on the same
   * physics as the hand. Far: a CUT - dip out, the page is put where it needs
   * to be, come back up on the piece - the way a film cuts rather than pans
   * across a city.
   */
  Stream.prototype.centre = function (p, instant) {
    var to = this.anchor(p);
    if (to === null) return;
    if (instant || RM.matches) { if (this.glide) this.glide.stop(); jump(to); if (this.glide) this.glide.sync(); this.focus(p, true); return; }
    var from = this.atEnd ? this.slides.length : (this.pos < 0 ? 0 : this.pos);
    if (Math.abs(p - from) > 3 || Math.abs(to - window.scrollY) > window.innerHeight * 3) { this.cut(p); return; }
    if (this.glide) this.glide.goTo(to);
    else window.scrollTo({ top: to, left: 0, behavior: 'smooth' });
  };

  Stream.prototype.cut = function (p, between) {
    var self = this;
    var root = this.root;
    if (this.glide) this.glide.stop();
    root.classList.add('is-cutting');
    clearTimeout(this.cutT);
    this.cutT = setTimeout(function () {
      if (between) between();
      var slide = self.slides[p];
      var img = slide && slide.querySelector('.ls-slide__img');
      for (var k = Math.max(0, p - 1); k <= Math.min(self.slides.length - 1, p + 1); k++) self.warm(self.slides[k]);
      var to = self.anchor(p);
      if (to !== null) jump(to);
      if (self.glide) self.glide.sync();
      if (slide) self.focus(p, true);
      var up = function () {
        if (up.done) return;
        up.done = true;
        requestAnimationFrame(function () { root.classList.remove('is-cutting'); });
      };
      /* Come back up on the picture, not an empty frame - but never wait long. */
      if (img && !(img.complete && img.naturalWidth) && img.decode) {
        img.decode().then(up, up);
        setTimeout(up, 650);
      } else {
        up();
      }
    }, RM.matches ? 0 : 200);
  };

  Stream.prototype.destroy = function () {
    if (this.band) this.band.disconnect();
    if (this.mid) this.mid.disconnect();
    if (this.near) this.near.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.glide) this.glide.destroy();
    window.removeEventListener('resize', this.onResize);
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  };

  /* ============================================================ THE GLIDE */

  function Glide(stream) {
    var self = this;
    var d = stream.root.dataset;
    this.s = stream;
    this.g = clamp(num(d.glide, 20) / 100, 0.04, 0.3);
    this.breath = clamp(num(d.breath, 3) / 100, 0, 0.1);
    this.raf = 0;
    this.lastT = 0;
    this.lastInput = -1e9;
    this.cadence = 0;
    this.travel = false;
    this.sync();

    this.tick = function (t) { self.frame(t); };

    var lastWheel = 0;
    this.onWheel = function (e) {
      var now = e.timeStamp || performance.now();
      var gap = now - lastWheel;
      lastWheel = now;
      if (e.ctrlKey || e.defaultPrevented || !self.eligible()) return;
      /* Anything that scrolls itself keeps its own wheel. The room bar does
         not qualify: a sideways swipe is left to it below, but a mouse wheel
         over it moves the page - right after choosing a room, that is exactly
         where the pointer is. */
      if (e.target && e.target.closest && e.target.closest('.ls-sheet, [data-ls-cart], .lmc, .lmd__panel, [data-lenis-prevent], textarea, select')) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      var unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
      /* A mouse notch: whole lines, or a full step on the wheel's own 120
         grid arriving on its own. A trackpad streams small steps every few
         milliseconds and never qualifies - it glides by distance instead. */
      var abs = Math.abs(e.deltaY);
      var notch = e.deltaMode !== 0 || (gap >= 25 && abs >= 50 && e.wheelDeltaY && e.wheelDeltaY % 120 === 0);
      e.preventDefault();
      self.push(e.deltaY * unit, notch);
    };

    this.onKey = function (e) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || !self.eligible()) return;
      var t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable], .ls-sheet, .lmd, .lmc')) return;
      var k = e.key;
      if (k === ' ' && t && t.closest && t.closest('button, a')) return;   /* Space presses the focused control */
      var dir = (k === 'ArrowDown' || k === 'PageDown' || (k === ' ' && !e.shiftKey)) ? 1
              : (k === 'ArrowUp' || k === 'PageUp' || (k === ' ' && e.shiftKey)) ? -1 : 0;
      if (!dir) return;
      var to = self.s.step(dir, self.raf ? self.target : window.scrollY);
      if (to === null) return;
      e.preventDefault();
      self.goTo(to);
    };

    /* It never fights anyone. A scroll it did not make - the scrollbar, a
       keyboard it let through, find-in-page, a link - hands the page back. */
    this.onScroll = function () {
      if (self.raf && Math.abs(window.scrollY - self.applied) > 4) self.stop();
    };
    this.onHide = function () { if (document.hidden) self.stop(); };

    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onHide);
  }

  Glide.prototype.eligible = function () {
    var root = this.s.root;
    if (root.dataset.mode !== 'immersive' || root.dataset.snap !== '1') return false;
    if (document.documentElement.style.overflow === 'hidden') return false;   /* a drawer holds the page */
    var scrim = this.scrim || (this.scrim = root.querySelector('[data-ls-scrim]'));
    return !(scrim && scrim.classList.contains('is-open'));
  };

  /* Take the page as it stands. */
  Glide.prototype.sync = function () {
    var y = window.scrollY;
    this.target = this.relay = this.current = this.applied = this.from = y;
    this.vel = 0;
    this.aligned = true;
    this.motion();
  };

  Glide.prototype.push = function (dy, notch) {
    var now = performance.now();
    /* At rest, a gesture starts from the page exactly as it stands - whatever
       moved it last (a keyboard, the scrollbar, a link) is respected. */
    if (!this.raf) { this.sync(); }
    /* A new gesture begins at the piece the page is resting on or heading for,
       so a second notch in mid-glide goes one further, not to the same place. */
    if (this.aligned) this.from = this.target;
    var gap = now - this.lastInput;
    if (gap < 800) this.cadence = gap > this.cadence ? gap : this.cadence + (gap - this.cadence) * 0.2;
    this.lastInput = now;

    /* A notch is a decision, not a distance: one notch, one piece, counted
       from wherever the page is already heading - three quick notches are
       three pieces. Outside the stream a notch is an ordinary scroll. */
    if (notch) {
      var to = this.s.step(dy > 0 ? 1 : -1, this.target);
      if (to !== null) {
        this.target = to;
        this.aligned = true;
        this.start();
        return;
      }
    }
    this.aligned = false;
    this.target = clamp(this.target + dy, 0, this.s.maxScroll());
    this.start();
  };

  Glide.prototype.goTo = function (y) {
    if (!this.raf) { var sy = window.scrollY; this.relay = this.current = this.applied = sy; }
    this.target = clamp(y, 0, this.s.maxScroll());
    this.aligned = true;
    this.lastInput = performance.now() - 1000;
    this.start();
  };

  Glide.prototype.start = function () {
    if (!this.raf) { this.lastT = 0; this.raf = requestAnimationFrame(this.tick); }
  };

  Glide.prototype.stop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastT = 0;
    this.sync();
  };

  Glide.prototype.frame = function (t) {
    this.raf = 0;
    var dt = this.lastT ? t - this.lastT : 16.7;
    this.lastT = t;
    var since = performance.now() - this.lastInput;

    /* A stalled tab or a long frame: take the page as it is rather than leap. */
    if (dt > 250 && since > 420) { this.stop(); return; }

    var c = clamp(dt / 1000, 1 / 240, 1 / 30);
    var f = c * 60;

    /* The hand has stopped: settle on a piece. The pause follows the
       visitor's own rhythm - a slow, deliberate scroller is not interrupted,
       a quick one is not kept waiting. */
    if (!this.aligned && since > clamp(this.cadence * 1.7, 180, 420)) {
      var r = this.s.land(this.target, this.from);
      if (r !== null) this.target = r;
      this.aligned = true;
    }

    /* Nearly there: the pull tightens, so it lands rather than drifts in. */
    var nearly = since > 90 && Math.abs(this.target - this.current) < 10;
    var k = clamp(this.g + (0.32 - this.g) * (nearly ? 0.6 : 0), 0.005, 0.95);
    var prev = this.current;
    this.relay += (this.target - this.relay) * (1 - Math.pow(1 - clamp(k * 1.8, 0.005, 0.95), f));
    this.current += (this.relay - this.current) * (1 - Math.pow(1 - k, f));
    /* The last fraction of a pixel is placed, not crept through: below 0.4px
       nothing moves that an eye can see, only a loop that has not finished. */
    if (this.aligned && Math.abs(this.target - this.current) < 0.4 && Math.abs(this.target - this.relay) < 0.4) {
      this.current = this.relay = this.target;
    }

    this.applied = this.current;
    jump(this.current);

    var inst = (this.current - prev) / c;
    this.vel += (inst - this.vel) * (1 - Math.pow(0.8, f));

    if (this.aligned && this.current === this.target) {
      this.vel = 0;
      this.motion();
      this.lastT = 0;
      return;
    }
    this.motion();
    this.raf = requestAnimationFrame(this.tick);
  };

  /*
   * In fast travel the pieces step back a touch and the label lets go; at
   * rest the piece is full size, flat and sharp, and the label resolves. The
   * product is never distorted - only, briefly, a little further away.
   */
  Glide.prototype.motion = function () {
    var v = Math.abs(this.vel || 0);
    if (this.breath && this.s.nearSet) {
      /*
       * Only the few pieces near the glass are written to, one property each.
       * (A variable on the whole stream cost a style pass over all eighty
       * pieces every frame - measured, it tripled the long frames.)
       */
      var sc = 1 - this.breath * smooth(clamp((v - 300) / 2600, 0, 1));
      var str = sc > 0.9995 ? '1' : sc.toFixed(4);
      if (str !== this.s.breathNow) {
        this.s.breathNow = str;
        var val = str === '1' ? '' : str;
        for (var i = 0; i < this.s.nearSet.length; i++) {
          var fr = this.s.nearSet[i].querySelector('.ls-slide__frame');
          if (fr) fr.style.scale = val;
        }
      }
    }
    var on = this.travel ? v > 420 : v > 1400;
    if (on !== this.travel) { this.travel = on; this.s.root.classList.toggle('is-travel', on); }
  };

  Glide.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onHide);
  };

  /* =============================================================== THE WARP */

  /*
   * One thread per piece in the room down the left edge. The lit thread is
   * where you are and its two neighbours part a little. Under a mouse the
   * threads swell toward the pointer, a small plate names the piece each one
   * stands for, and a click carries you to it. The plate number above opens
   * the whole archive.
   */
  function Warp(root, stream) {
    var self = this;
    this.root = root;
    this.stream = stream;
    this.reed = root.querySelector('[data-ls-reed]');
    this.n = root.querySelector('[data-ls-index]');
    this.total = root.querySelector('[data-ls-total]');
    this.tip = root.querySelector('[data-ls-tip]');
    this.warpEl = root.querySelector('.ls-warp');
    this.cur = -1;
    this.bent = [];
    this.tipI = -1;
    this.ticks = [];
    if (!this.reed) return;
    this.allTicks = slice(this.reed.querySelectorAll('.ls-warp__t'));

    var plate = root.querySelector('[data-ls-warp-plate]');
    if (plate) {
      plate.addEventListener('click', function () {
        var g = root.querySelector('[data-ls-mode="grid"]');
        if (g) g.click();
      });
    }

    if (!FINE) return;
    var raf = 0;
    var py = 0;
    this.reed.addEventListener('pointermove', function (e) {
      py = e.clientY;
      if (!raf) raf = requestAnimationFrame(function () { raf = 0; self.hover(py); });
    });
    this.reed.addEventListener('pointerleave', function () { self.unhover(); });
    this.reed.addEventListener('click', function (e) {
      var i = self.at(e.clientY);
      if (i >= 0 && (i !== stream.pos || stream.atEnd)) stream.centre(i);
    });
  }

  Warp.prototype.rebuild = function (key) {
    if (!this.reed) return;
    this.allTicks.forEach(function (t) { t.classList.remove('is-lit', 'is-near'); });
    this.ticks = this.allTicks.filter(function (t) { return key === 'all' || t.dataset.room === key; });
    this.root.style.setProperty('--ls-n', String(this.ticks.length));
    if (this.total) this.total.textContent = String(this.ticks.length);
    this.cur = -1;
    this.unhover();
  };

  /* The thread nearest a height on the screen. They are evenly spaced. */
  Warp.prototype.at = function (y) {
    var n = this.ticks.length;
    if (!n) return -1;
    var a = this.ticks[0].getBoundingClientRect().top;
    var b = this.ticks[n - 1].getBoundingClientRect().top;
    if (n === 1 || b <= a) return 0;
    return clamp(Math.round((y - a) / ((b - a) / (n - 1))), 0, n - 1);
  };

  Warp.prototype.hover = function (y) {
    var i = this.at(y);
    if (i < 0) return;
    var n = this.ticks.length;
    this.reed.classList.add('is-hover');
    this.bent.forEach(function (t) { t.style.removeProperty('--f'); });
    this.bent = [];
    for (var k = Math.max(0, i - 5); k <= Math.min(n - 1, i + 5); k++) {
      var dd = Math.abs(k - i);
      var f = 1 + 2.3 * Math.pow(1 - dd / 6, 2);
      if (k === this.cur) f = Math.max(f, 2.6);
      this.ticks[k].style.setProperty('--f', f.toFixed(2));
      this.bent.push(this.ticks[k]);
    }
    if (!this.tip || !this.warpEl) return;
    if (i !== this.tipI) {
      this.tipI = i;
      var t = this.ticks[i];
      var span = this.tip.querySelector('span');
      var img = this.tip.querySelector('img');
      if (!img) {
        /* Decorative: the name beside it says everything the picture does. */
        img = document.createElement('img');
        img.alt = '';
        img.width = 44;
        img.height = 56;
        img.decoding = 'async';
        this.tip.insertBefore(img, span);
      }
      var src = t.getAttribute('data-src');
      if (src) { img.src = src; img.style.visibility = ''; } else { img.style.visibility = 'hidden'; }
      if (span) {
        var slide = this.stream.slides[i];
        var fam = slide && slide.querySelector('.ls-slide__family');
        var name = slide && slide.querySelector('.ls-slide__name');
        span.textContent = '';
        var b = document.createElement('b');
        b.textContent = pad(i + 1) + (fam ? '  ' + fam.textContent.trim() : '');
        span.appendChild(b);
        span.appendChild(document.createTextNode(name ? name.textContent.trim() : ''));
      }
    }
    var tr = this.ticks[i].getBoundingClientRect();
    var wr = this.warpEl.getBoundingClientRect();
    this.tip.style.top = (tr.top + tr.height / 2 - wr.top).toFixed(1) + 'px';
    this.tip.classList.add('is-on');
  };

  Warp.prototype.unhover = function () {
    if (!this.reed) return;
    this.reed.classList.remove('is-hover');
    this.bent.forEach(function (t) { t.style.removeProperty('--f'); });
    this.bent = [];
    this.tipI = -1;
    if (this.tip) this.tip.classList.remove('is-on');
  };

  Warp.prototype.set = function (i) {
    var T = this.ticks;
    if (T && T.length) {
      if (this.cur >= 0) {
        for (var k = this.cur - 1; k <= this.cur + 1; k++) {
          if (T[k]) T[k].classList.remove('is-lit', 'is-near');
        }
      }
      if (T[i - 1]) T[i - 1].classList.add('is-near');
      if (T[i + 1]) T[i + 1].classList.add('is-near');
      if (T[i]) T[i].classList.add('is-lit');
    }
    this.cur = i;

    /* The number turns over rather than jumping. */
    var n = this.n;
    var txt = pad(i + 1);
    if (n && n.textContent !== txt) {
      if (RM.matches) { n.textContent = txt; return; }
      n.classList.add('is-turning');
      clearTimeout(this.nT);
      this.nT = setTimeout(function () {
        n.textContent = txt;
        n.classList.remove('is-turning');
      }, 120);
    }
  };

  /* ============================================================= THE ROOMS */

  /*
   * One tap, and the stream is that family alone. From the entrance the page
   * is carried down to the room's first piece; from inside the stream it is a
   * cut - the pieces dip out, the room changes behind the dip, the first piece
   * of the new room comes up in the middle. The room goes into the address
   * with replaceState, so a piece opened from Kazak comes back to Kazak, and
   * the back button is not filled with rooms.
   */
  function Rooms(root, stream, modes) {
    var self = this;
    this.root = root;
    this.stream = stream;
    this.modes = modes;
    this.nav = root.querySelector('[data-ls-rooms]');
    if (!this.nav) return;
    this.track = root.querySelector('[data-ls-rooms-track]');
    this.btns = slice(root.querySelectorAll('[data-ls-room]'));
    this.keys = this.btns.map(function (b) { return b.dataset.lsRoom; });
    this.btns.forEach(function (b) {
      b.addEventListener('click', function () { self.go(b.dataset.lsRoom); });
    });

    /* The count in the heading speaks for the room. */
    this.count = root.querySelector('[data-ls-count]');
    this.countWord = root.querySelector('[data-ls-count-word]');

    /* Filters and sorting keep the room. */
    root.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-ls-keep-room]');
      if (!a || root.dataset.room === 'all' || !a.getAttribute('href')) return;
      try {
        var u = new URL(a.getAttribute('href'), location.href);
        u.searchParams.set('room', root.dataset.room);
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (err) {}
    });

    this.paint(true);
  }

  Rooms.prototype.name = function (key) {
    var b = this.btns[this.keys.indexOf(key)];
    return b ? b.textContent.trim() : '';
  };

  Rooms.prototype.next = function (key) {
    if (key === 'all') return null;
    var i = this.keys.indexOf(key);
    return i > 0 && i < this.keys.length - 1 ? this.keys[i + 1] : this.keys[1] !== key ? this.keys[1] : null;
  };

  /* Everything that says which room this is. */
  Rooms.prototype.paint = function (first) {
    var root = this.root;
    var key = root.dataset.room || 'all';
    var self = this;
    this.btns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.lsRoom === key)); });

    var n = this.stream ? this.stream.slides.length : 0;
    if (this.count) this.count.textContent = String(n);
    if (this.countWord) this.countWord.textContent = n === 1 ? (root.dataset.piece || 'piece') : (root.dataset.pieces || 'pieces');

    var end = root.querySelector('[data-ls-end]');
    if (end) {
      var title = end.querySelector('[data-ls-end-title]');
      if (title) title.textContent = key === 'all' ? end.dataset.all : (end.dataset.roomWords || '').replace('[room]', this.name(key));
      var nb = end.querySelector('[data-ls-end-next]');
      var nx = this.next(key);
      if (nb) {
        nb.hidden = !nx;
        nb.dataset.room = nx || '';
        var nm = nb.querySelector('[data-ls-end-next-name]');
        if (nm) nm.textContent = nx ? this.name(nx) : '';
      }
    }

    /* The room in focus is kept in view on the track, without a jump. */
    var on = this.btns[this.keys.indexOf(key)];
    if (on && this.track && this.track.scrollWidth > this.track.clientWidth + 2) {
      /* Measured against the track itself - the button's offsetParent is the
         whole bar, switch included, which centred it off the left edge. */
      var tr = this.track.getBoundingClientRect();
      var br = on.getBoundingClientRect();
      var x = this.track.scrollLeft + (br.left - tr.left) - (tr.width - br.width) / 2;
      this.track.scrollTo({ left: Math.max(0, x), behavior: first || RM.matches ? 'auto' : 'smooth' });
    }

    if (!first) {
      try {
        var u = new URL(location.href);
        if (key === 'all') u.searchParams.delete('room'); else u.searchParams.set('room', key);
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      } catch (e) {}
    }
    rememberRoom(root, key === 'all' ? null : this.name(key));
  };

  Rooms.prototype.go = function (key) {
    var root = this.root;
    var stream = this.stream;
    var self = this;
    if (this.keys.indexOf(key) < 0) return;

    var change = function () {
      root.dataset.room = key;
      if (stream) stream.room();
      self.paint(false);
    };

    if (key === (root.dataset.room || 'all')) {
      if (stream && this.modes.mode === 'immersive') stream.centre(0);
      return;
    }

    if (this.modes && this.modes.mode === 'grid') {
      change();
      var grid = root.querySelector('.ls-grid');
      if (grid && grid.getBoundingClientRect().top < 0) jump(window.scrollY + grid.getBoundingClientRect().top - (stream ? stream.covered() : 0) - 12);
      return;
    }
    if (!stream) { change(); return; }

    /* Inside the stream: a cut, with the room changed behind the dip. */
    if (stream.inZone()) { stream.cut(0, change); return; }

    /* At the entrance: the room changes where you stand, then carries you in. */
    change();
    requestAnimationFrame(function () { stream.centre(0); });
  };

  /* ======================================================== THE END OF A ROOM */

  function End(root, stream, rooms) {
    var end = root.querySelector('[data-ls-end]');
    if (!end || !stream) return;
    var next = end.querySelector('[data-ls-end-next]');
    var ask = end.querySelector('[data-ls-end-ask]');
    var top = end.querySelector('[data-ls-end-top]');
    if (next && rooms && rooms.nav) next.addEventListener('click', function () { if (next.dataset.room) rooms.go(next.dataset.room); });
    if (top) top.addEventListener('click', function () { stream.centre(0); });

    /* The house is the Desk. Its own mark opens it, so it is asked through the
       mark rather than reached into - the Desk keeps its one way of opening. */
    var mark = document.querySelector('.lmd [data-lmd-fan-toggle]');
    if (ask && mark) {
      ask.hidden = false;
      ask.addEventListener('click', function () {
        var fan = document.querySelector('.lmd [data-lmd-fan]');
        if (fan && fan.classList.contains('is-open')) return;
        mark.click();
      });
    }
  }

  /* ============================================================ THE POINTER */

  /*
   * On a desk the pointer carries one word: "See the piece" over the piece in
   * focus, and a neighbour's own number over a neighbour - which a click
   * brings forward. It follows the hand a little behind, the same two-stage
   * ease as the page, and steps away in fast travel or over anything else.
   */
  function Pointer(root, stream) {
    var el = root.querySelector('[data-ls-cursor]');
    if (!el || !stream || !FINE || RM.matches) return;
    var txt = el.querySelector('[data-ls-cursor-text]');
    var open = el.dataset.open || '';
    var x = 0, y = 0, cx = 0, cy = 0, raf = 0, on = false, last = '';

    var loop = function () {
      raf = 0;
      cx += (x - cx) * 0.24;
      cy += (y - cy) * 0.24;
      el.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
      if (on && (Math.abs(x - cx) > 0.2 || Math.abs(y - cy) > 0.2)) raf = requestAnimationFrame(loop);
    };
    var show = function (word) {
      if (word !== last) { last = word; txt.textContent = word; }
      if (!on) { on = true; cx = x; cy = y; el.classList.add('is-on'); }
      if (!raf) raf = requestAnimationFrame(loop);
    };
    var hide = function () {
      if (!on) return;
      on = false;
      el.classList.remove('is-on');
    };

    stream.el.addEventListener('pointermove', function (e) {
      x = e.clientX; y = e.clientY;
      if (root.dataset.mode !== 'immersive' || root.classList.contains('is-travel')) { hide(); return; }
      var f = e.target.closest && e.target.closest('.ls-slide__frame');
      if (!f) { hide(); return; }
      var p = stream.slides.indexOf(f.closest('.ls-slide'));
      if (p < 0) { hide(); return; }
      show(p === stream.pos && !stream.atEnd ? open : stream.prefix + ' ' + pad(p + 1));
    });
    stream.el.addEventListener('pointerleave', hide);
    root.addEventListener('ls:focus', function () {
      if (!on) return;
      var f = document.elementFromPoint(x, y);
      var s = f && f.closest && f.closest('.ls-slide');
      var p = s ? stream.slides.indexOf(s) : -1;
      if (p === stream.pos) show(open);
    });
    window.addEventListener('scroll', function () { if (root.classList.contains('is-travel')) hide(); }, { passive: true });
  }

  /*
   * The archive's pictures arrive rather than appear. The hiding class is
   * armed here, from the script, so a failed bundle can never leave a blank
   * grid. The first rows are fetched the moment the visitor reaches for the
   * archive, so they are usually in by the time it opens.
   */
  function Shots(root) {
    var imgs = slice(root.querySelectorAll('.ls-card__shot img'));
    if (!imgs.length) return;
    root.classList.add('is-live');
    imgs.forEach(function (img) {
      var on = function () { img.classList.add('is-in'); };
      if (img.complete && img.naturalWidth) on();
      else {
        img.addEventListener('load', on, { once: true });
        img.addEventListener('error', on, { once: true });
      }
    });
    var warm = function () {
      for (var k = 0; k < Math.min(12, imgs.length); k++) {
        if (imgs[k].loading === 'lazy') imgs[k].loading = 'eager';
      }
    };
    root.querySelectorAll('[data-ls-mode="grid"], [data-ls-warp-plate]').forEach(function (b) {
      b.addEventListener('pointerenter', warm, { once: true });
      b.addEventListener('pointerdown', warm, { once: true });
      b.addEventListener('focus', warm, { once: true });
    });
    if (root.dataset.mode === 'grid') warm();
  }

  /* ====================================================== IMMERSIVE / GRID */

  function Modes(root, stream) {
    var self = this;
    this.root = root;
    this.stream = stream;
    this.mode = root.dataset.mode || 'immersive';

    this.btns = slice(root.querySelectorAll('[data-ls-mode]'));
    this.btns.forEach(function (b) {
      b.addEventListener('click', function () { self.set(b.dataset.lsMode); });
    });

    /* A card opens its piece in the stream rather than navigating — the
       archive and the gallery are one place, seen from two distances. */
    root.querySelectorAll('.ls-card').forEach(function (c) {
      c.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
        if (root.dataset.gridOpens === 'product' || !self.stream) return;
        e.preventDefault();
        self.set('immersive', self.posOf(parseInt(c.dataset.i, 10)));
      });
    });

    try {
      var saved = sessionStorage.getItem('ls-mode');
      if (saved && saved !== this.mode) this.set(saved, null, true);
    } catch (e) {}

    this.sync();
  }

  /* A card's place in the room, from its place in the whole page. */
  Modes.prototype.posOf = function (i) {
    if (!this.stream) return 0;
    var p = -1;
    this.stream.slides.forEach(function (s, k) { if (parseInt(s.dataset.i, 10) === i) p = k; });
    return p < 0 ? 0 : p;
  };

  Modes.prototype.sync = function () {
    var m = this.mode;
    this.btns.forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.lsMode === m));
    });
  };

  Modes.prototype.set = function (next, pos, silent) {
    var stream = this.stream;
    if (next === this.mode) {
      if (next === 'immersive' && pos != null && stream) stream.centre(pos);
      return;
    }

    var root = this.root;
    var p = pos != null ? pos : (stream ? Math.max(0, stream.pos) : 0);
    var gi = stream && stream.slides[p] ? stream.slides[p].dataset.i : String(p);

    var from = silent || RM.matches ? null : this.shot(this.mode, gi);
    if (stream && stream.glide) stream.glide.stop();

    this.mode = next;
    root.dataset.mode = next;
    this.sync();
    try { sessionStorage.setItem('ls-mode', next); } catch (e) {}

    if (next === 'immersive') {
      if (stream) { stream.stale = true; stream.centre(p, true); stream.request(); }
    } else {
      var card = root.querySelector('.ls-card[data-i="' + gi + '"]');
      if (card) card.scrollIntoView({ block: 'center', behavior: 'instant' });
    }

    if (from) {
      var to = this.shot(next, gi);
      if (to) this.morph(from, to);
    }

    root.dispatchEvent(new CustomEvent('ls:mode', { detail: { mode: next, index: p } }));
  };

  /* Measure the piece as it currently sits, in either world. */
  Modes.prototype.shot = function (mode, gi) {
    var el = mode === 'immersive'
      ? this.root.querySelector('.ls-slide[data-i="' + gi + '"] .ls-slide__img')
      : this.root.querySelector('.ls-card[data-i="' + gi + '"] img');
    if (!el) return null;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return { el: el, r: r, src: el.currentSrc || el.src };
  };

  /*
   * The same piece, carried from one world to the other. Nothing dissolves and
   * nothing reloads — the eye keeps hold of one object while the space around
   * it changes scale. That is what makes the two modes feel like one place.
   */
  Modes.prototype.morph = function (from, to) {
    var fly = document.createElement('img');
    fly.className = 'ls-morph';
    fly.src = from.src;
    fly.setAttribute('aria-hidden', 'true');
    fly.style.left = '0'; fly.style.top = '0';
    fly.style.width = from.r.width + 'px';
    fly.style.height = from.r.height + 'px';
    fly.style.transform = 'translate3d(' + from.r.left + 'px,' + from.r.top + 'px,0)';
    document.body.appendChild(fly);

    to.el.style.opacity = '0';

    var sx = to.r.width / from.r.width;
    var sy = to.r.height / from.r.height;

    var anim = fly.animate([
      { transform: 'translate3d(' + from.r.left + 'px,' + from.r.top + 'px,0) scale(1,1)', opacity: 1 },
      { transform: 'translate3d(' + to.r.left + 'px,' + to.r.top + 'px,0) scale(' + sx + ',' + sy + ')', opacity: 1 }
    ], { duration: T_SLOW, easing: MOVE, fill: 'both' });

    fly.style.transformOrigin = '0 0';

    var done = function () {
      to.el.style.opacity = '';
      if (fly.parentNode) fly.parentNode.removeChild(fly);
    };
    anim.addEventListener('finish', done);
    anim.addEventListener('cancel', done);
    setTimeout(done, 900);
  };

  /* ============================================================ SHEETS */

  function Sheets(root) {
    var scrim = root.querySelector('[data-ls-scrim]');

    function close() {
      root.querySelectorAll('.ls-sheet.is-open').forEach(function (s) { s.classList.remove('is-open'); });
      if (scrim) scrim.classList.remove('is-open');
      document.documentElement.style.overflow = '';
    }

    root.querySelectorAll('[data-ls-open]').forEach(function (b) {
      b.addEventListener('click', function () {
        var t = root.querySelector('#' + b.dataset.lsOpen);
        if (!t) return;
        close();
        t.classList.add('is-open');
        if (scrim) scrim.classList.add('is-open');
      });
    });

    root.querySelectorAll('[data-ls-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    if (scrim) scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ========================================================= ACCORDIONS */

  function accordions(scope) {
    scope.querySelectorAll('.ls-acc__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var row = btn.closest('.ls-acc__row');
        var on = row.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(on));
      });
    });
  }

  /* ============================================================ GALLERY */

  function Gallery(root) {
    var self = this;
    this.rail = root.querySelector('.ls-gal');
    if (!this.rail) return;

    this.items = Array.prototype.slice.call(this.rail.querySelectorAll('.ls-gal__item'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('.ls-gal__dot'));

    this.dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        self.rail.scrollTo({ left: self.rail.clientWidth * i, behavior: RM.matches ? 'auto' : 'smooth' });
      });
    });

    var tick = false;
    this.rail.addEventListener('scroll', function () {
      if (tick) return;
      tick = true;
      requestAnimationFrame(function () {
        tick = false;
        var i = Math.round(self.rail.scrollLeft / Math.max(1, self.rail.clientWidth));
        self.dots.forEach(function (d, j) { d.setAttribute('aria-current', String(j === i)); });
      });
    }, { passive: true });

    /* ------------------------------------------------- THE CLOSE LOOK
     * PhotoSwipe (dimsemenov/PhotoSwipe 5.4.4, MIT), served from the theme's
     * own assets: pinch and double-tap down to the knots, swipe between the
     * photographs, drag down to put the piece back. It opens out of the
     * photograph that was touched and closes back into it - the rail follows
     * along, so the piece always returns to where the eye left it. The
     * piece's name and the house's door ride along at the foot.
     *
     * Loaded only when wanted: in idle time after the page settles, or the
     * moment a finger touches the gallery, whichever comes first. If the
     * module cannot load, the house's own close look below still opens. */
    var psSrc = root.getAttribute('data-pswp');
    var psCss = root.getAttribute('data-pswp-css');
    var PS = null, psWait = null;
    function loadPS() {
      if (PS) return Promise.resolve(PS);
      if (psWait) return psWait;
      if (!psSrc) return Promise.reject(new Error('no photoswipe'));
      var css = new Promise(function (done) {
        if (!psCss || document.querySelector('link[data-pswp-css]')) return done();
        var l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = psCss;
        l.setAttribute('data-pswp-css', '');
        l.onload = l.onerror = function () { done(); };
        document.head.appendChild(l);
      });
      /* Reached through a module script written here rather than import():
         Shopify's asset minifier rewrites import() into require(), which no
         browser has - the close look silently fell back until 14 Oct. */
      var mod = new Promise(function (done, fail) {
        if (window.__lmPS) return done(window.__lmPS);
        var t = setTimeout(function () { fail(new Error('photoswipe timed out')); }, 8000);
        window.addEventListener('lm:pswp', function () { clearTimeout(t); done(window.__lmPS); }, { once: true });
        var s = document.createElement('script');
        s.type = 'module';
        s.onerror = function () { clearTimeout(t); fail(new Error('photoswipe did not load')); };
        s.textContent = 'import P from ' + JSON.stringify(psSrc) + ';window.__lmPS=P;window.dispatchEvent(new Event("lm:pswp"));';
        document.head.appendChild(s);
      });
      psWait = Promise.all([mod, css]).then(function (r) { PS = r[0]; return PS; });
      psWait.catch(function () { psWait = null; });
      return psWait;
    }
    var warm = function () { loadPS().catch(function () {}); };
    this.rail.addEventListener('pointerdown', warm, { once: true, passive: true });
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
    else setTimeout(warm, 2500);

    var two = function (n) { return (n < 10 ? '0' : '') + n; };
    function caption() {
      var h = root.querySelector('.ls-pdp__title');
      if (!h) return '';
      return Array.prototype.map.call(h.children, function (c) { return c.textContent.trim(); })
        .filter(Boolean).join(' · ');
    }
    function openPS(index) {
      return loadPS().then(function (PhotoSwipe) {
        var data = self.items.map(function (it) {
          var img = it.querySelector('img');
          return {
            src: img.getAttribute('data-zoom') || img.currentSrc || img.src,
            msrc: img.currentSrc || img.src,
            width: +img.getAttribute('data-zoom-w') || img.naturalWidth || 1600,
            height: +img.getAttribute('data-zoom-h') || img.naturalHeight || 1600,
            alt: img.alt || '',
            element: img
          };
        });
        var still = RM.matches;
        var pswp = new PhotoSwipe({
          dataSource: data,
          index: index,
          mainClass: 'lm-pswp',
          bgOpacity: 1,
          showHideAnimationType: still ? 'fade' : 'zoom',
          showAnimationDuration: still ? 0 : 440,
          hideAnimationDuration: still ? 0 : 380,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          /* fit first; a double-tap goes to the knots, never past the file */
          initialZoomLevel: 'fit',
          secondaryZoomLevel: function (zl) { return Math.min(zl.fit * 3, 1); },
          maxZoomLevel: 1.6,
          wheelToZoom: true,
          padding: { top: 60, bottom: 92, left: 14, right: 14 },
          counter: false,
          closeTitle: root.getAttribute('data-pswp-close') || 'Close',
          zoomTitle: root.getAttribute('data-pswp-zoom') || 'Close look',
          arrowPrevTitle: 'Previous',
          arrowNextTitle: 'Next',
          errorMsg: 'This photograph could not be loaded.'
        });
        pswp.on('uiRegister', function () {
          pswp.ui.registerElement({
            name: 'lm-count', order: 5, isButton: false, appendTo: 'bar',
            onInit: function (el, p) {
              var set = function () { el.textContent = two(p.currIndex + 1) + ' / ' + two(p.getNumItems()); };
              set();
              p.on('change', set);
              if (p.getNumItems() < 2) el.style.visibility = 'hidden';
            }
          });
          pswp.ui.registerElement({
            name: 'lm-foot', order: 9, isButton: false, appendTo: 'root',
            onInit: function (el) {
              var name = document.createElement('span');
              name.className = 'pswp__lm-name';
              name.textContent = caption();
              el.appendChild(name);
              var ask = root.querySelector('.ls-ask__link[data-ls-ask]');
              if (ask) {
                var a = ask.cloneNode(true);
                a.className = 'pswp__lm-ask';
                el.appendChild(a);
              }
            }
          });
        });
        /* the rail follows the lightbox, so closing lands on the right photograph */
        pswp.on('change', function () {
          self.rail.scrollTo({ left: self.rail.clientWidth * pswp.currIndex, behavior: 'auto' });
        });
        pswp.init();
        return pswp;
      });
    }

    this.zoom = root.querySelector('[data-ls-zoom]');
    if (this.zoom) {
      this.zimg = this.zoom.querySelector('img');
      var houseZoom = function (img) {
        self.zimg.src = img.dataset.zoom || img.currentSrc || img.src;
        self.zimg.alt = img.alt || '';
        self.zoom.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        /* Opens already leaned in, then centres, so it reads as stepping
           closer rather than as a magnifier snapping on. */
        requestAnimationFrame(function () {
          self.zimg.style.width = Math.round(self.zoom.clientWidth * 1.9) + 'px';
          self.zoom.scrollLeft = (self.zoom.scrollWidth - self.zoom.clientWidth) / 2;
          self.zoom.scrollTop = (self.zoom.scrollHeight - self.zoom.clientHeight) / 2;
        });
      };
      this.items.forEach(function (it, i) {
        it.addEventListener('click', function () {
          var img = it.querySelector('img');
          if (!img) return;
          openPS(i).catch(function (err) {
            if (window.console) console.warn('[loomish] close look fell back:', err);
            houseZoom(img);
          });
        });
      });
      var shut = function () {
        self.zoom.classList.remove('is-open');
        document.documentElement.style.overflow = '';
      };
      this.zoom.addEventListener('click', shut);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.zoom.classList.contains('is-open')) shut();
      });
    }
  }

  /* =============================================================== CART */

  var Cart = {
    root: null,
    currency: 'USD',

    init: function () {
      var self = this;

      /*
       * The buttons are delegated from the document, not from the drawer —
       * the cart PAGE has quantity controls but no drawer, and gating this on
       * the drawer existing would leave that page's buttons inert.
       */
      document.addEventListener('click', function (e) {
        var open = e.target.closest('[data-ls-cart-open]');
        if (open) { e.preventDefault(); self.openDrawer(); return; }

        var add = e.target.closest('[data-ls-add]');
        if (add) { e.preventDefault(); self.add(add); return; }

        var q = e.target.closest('[data-ls-qty]');
        if (q) { e.preventDefault(); self.change(q.dataset.key, parseInt(q.dataset.qty, 10)); }
      });

      this.countEls = document.querySelectorAll('[data-ls-cart-count]');

      this.root = document.querySelector('[data-ls-cart]');
      if (!this.root) return;

      this.currency = this.root.dataset.currency || 'USD';
      this.scrim = this.root.querySelector('[data-ls-cart-scrim]');
      this.sheet = this.root.querySelector('[data-ls-cart-sheet]');
      this.drawer = this.root.querySelector('[data-ls-cart-drawer]');
      this.sheetBody = this.root.querySelector('[data-ls-sheet-body]');
      this.drawerBody = this.root.querySelector('[data-ls-drawer-body]');
      this.drawerFoot = this.root.querySelector('[data-ls-drawer-foot]');

      this.root.querySelectorAll('[data-ls-cart-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.close(); });
      });
      if (this.scrim) this.scrim.addEventListener('click', function () { self.close(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.close(); });
    },

    lock: function (on) {
      document.documentElement.style.overflow = on ? 'hidden' : '';
    },

    close: function () {
      if (this.sheet) this.sheet.classList.remove('is-open');
      if (this.drawer) this.drawer.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-open');
      this.lock(false);
    },

    add: function (btn) {
      var self = this;
      var id = btn.dataset.lsAdd;
      if (!id || btn.disabled) return;

      var label = btn.querySelector('[data-ls-add-label]');
      var was = label ? label.textContent : '';
      btn.disabled = true;
      if (label) label.textContent = btn.dataset.busy || 'Adding';

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          btn.disabled = false;
          if (label) label.textContent = was;
          if (!res.ok) {
            if (label) label.textContent = res.j.description || 'Unavailable';
            setTimeout(function () { if (label) label.textContent = was; }, 2400);
            return;
          }
          /* The selection drawer (sections/loomish-order) takes over when it is
             on the page; the rise sheet is only the fallback. */
          if (window.LoomishOrder) { self.refresh(); return window.LoomishOrder.open({ added: Number(id) }); }
          return self.refresh().then(function () { self.openSheet(); });
        })
        .catch(function () {
          btn.disabled = false;
          if (label) label.textContent = was;
        });
    },

    change: function (key, qty) {
      var self = this;
      if (!key) return;
      if (this.root) this.root.classList.add('is-busy');

      var page = document.querySelector('[data-ls-cartpage]');
      var host = page ? page.closest('.shopify-section') : null;

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          self.paint(c);

          /*
           * On the cart page the lines are Liquid, so Shopify re-renders them
           * rather than this file rebuilding the same markup a second time in
           * a second language. Two renderers for one list is how they drift.
           */
          if (!host) return;
          var id = host.id.replace('shopify-section-', '');
          return fetch(location.pathname + '?section_id=' + encodeURIComponent(id))
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var box = document.createElement('div');
              box.innerHTML = html;
              var next = box.querySelector('[data-ls-cartpage]');
              if (next) page.replaceWith(next);
            });
        })
        .catch(function () {})
        .then(function () { if (self.root) self.root.classList.remove('is-busy'); });
    },

    refresh: function () {
      var self = this;
      return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart.js' : '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { self.paint(c); return c; });
    },

    openSheet: function () {
      if (!this.sheet) return;
      if (this.drawer) this.drawer.classList.remove('is-open');
      this.sheet.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-open');
    },

    openDrawer: function () {
      var self = this;
      if (window.LoomishOrder) { window.LoomishOrder.open(); return; }
      this.refresh().then(function () {
        if (!self.drawer) return;
        if (self.sheet) self.sheet.classList.remove('is-open');
        self.drawer.classList.add('is-open');
        if (self.scrim) self.scrim.classList.add('is-open');
        self.lock(true);
      });
    },

    line: function (it, compact) {
      var img = it.image
        ? '<img class="ls-line__shot" src="' + it.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') +
          '" alt="" loading="lazy" width="66" height="88">'
        : '<span class="ls-line__shot"></span>';

      /* Titles carry their own size after the last dash — the same reading the
         collection uses, so the piece is named identically everywhere. */
      var parts = (it.product_title || it.title || '').split(/\s+-\s+/);
      var size = parts.length > 1 ? parts[parts.length - 1] : '';
      var name = parts.length > 2 ? parts.slice(1, -1).join(' ') : parts[0];

      return '<div class="ls-line">' +
        img +
        '<div><a class="ls-line__name" href="' + it.url + '">' + name + '</a>' +
        (size ? '<div class="ls-line__size">' + size + '</div>' : '') +
        '<div class="ls-line__price">' + money(it.final_line_price, this.currency) + '</div>' +
        (compact ? '' :
          '<div class="ls-qty">' +
          '<button type="button" data-ls-qty data-key="' + it.key + '" data-qty="' + (it.quantity - 1) + '" aria-label="Reduce">&minus;</button>' +
          '<output>' + it.quantity + '</output>' +
          '<button type="button" data-ls-qty data-key="' + it.key + '" data-qty="' + (it.quantity + 1) + '" aria-label="Increase">+</button>' +
          '</div>') +
        '</div>' +
        '<button type="button" class="ls-line__rm ls-label" data-ls-qty data-key="' + it.key + '" data-qty="0" aria-label="Remove">Remove</button>' +
        '</div>';
    },

    paint: function (c) {
      var self = this;
      if (!c) return;

      if (this.countEls) {
        this.countEls.forEach(function (e) {
          e.textContent = c.item_count > 0 ? String(c.item_count) : '';
          e.hidden = c.item_count === 0;
        });
      }

      if (!this.root) return;

      if (this.sheetBody) {
        var last = c.items && c.items.length ? c.items[c.items.length - 1] : null;
        this.sheetBody.innerHTML = last
          ? this.line(last, false) +
            '<div class="ls-tot"><div class="ls-tot__row ls-tot__row--sum"><span>Subtotal</span><span>' +
            money(c.total_price, this.currency) + '</span></div></div>'
          : '<div class="ls-cart__empty">Nothing here yet.</div>';
      }

      if (this.drawerBody) {
        this.drawerBody.innerHTML = c.items && c.items.length
          ? c.items.map(function (it) { return self.line(it, false); }).join('')
          : '<div class="ls-cart__empty">Your cart is empty.</div>';
      }

      if (this.drawerFoot) {
        this.drawerFoot.hidden = !(c.items && c.items.length);
        var sub = this.drawerFoot.querySelector('[data-ls-subtotal]');
        if (sub) sub.textContent = money(c.total_price, this.currency);
      }

      var head = this.root.querySelector('[data-ls-drawer-count]');
      if (head) head.textContent = '(' + (c.item_count || 0) + ')';
    }
  };

  /* ================================================================ RISE */

  /*
   * Nothing arrives all at once.
   *
   * Blocks marked [data-ls-rise] wait until the eye reaches them and then rise
   * on the stylesheet's own curve, staggered by their index. It is the same
   * scroll-triggered reveal the best editorial shops use, with two rules that
   * most implementations get wrong:
   *
   *   1. THE PAGE IS VISIBLE UNTIL JS ARMS IT. The hiding class goes on the
   *      root from script, so a failed or blocked bundle leaves a readable
   *      page rather than an invisible one. Hiding in CSS and revealing in JS
   *      is how a cart ends up permanently blank.
   *   2. IT ONLY EVER RUNS ONCE PER BLOCK. Unobserving on arrival means no
   *      element is measured again for the life of the page, and nothing
   *      re-animates when you scroll back up — which reads as a glitch.
   */
  function Rise(root) {
    var blocks = Array.prototype.slice.call(root.querySelectorAll('[data-ls-rise]'));
    if (!blocks.length) return;

    if (RM.matches || !('IntersectionObserver' in window)) {
      blocks.forEach(function (b) { b.classList.add('is-up'); });
      return;
    }

    /* Arm only now, so the page was never hidden without something to unhide it */
    document.documentElement.classList.add('ls-rise-on');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-up');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    blocks.forEach(function (b) { io.observe(b); });

    /* Anything already on screen goes up on the next frame rather than waiting
       for a scroll that may never come on a short cart. */
    requestAnimationFrame(function () {
      blocks.forEach(function (b) {
        var r = b.getBoundingClientRect();
        if (r.top < window.innerHeight) { b.classList.add('is-up'); io.unobserve(b); }
      });
    });
  }

  /* ============================================================ COMMITTED */

  /*
   * The press is acknowledged before the page has begun to go.
   *
   * A checkout button that sits inert while the browser thinks is the moment a
   * customer wonders whether it worked and presses again. The fill completes,
   * the mark leaves, and the button stops taking clicks — all in the same
   * frame as the submit, so nothing here can delay or block the form.
   */
  function commit(root) {
    var btn = root.querySelector('.ls-btn--go');
    if (!btn) return;
    var form = btn.closest('form');
    if (!form) return;
    form.addEventListener('submit', function () {
      btn.classList.add('is-going');
      /* Never disable it: a disabled button is not submitted by some browsers,
         and the name="checkout" on it is what tells Shopify where to go. */
    });
  }

  /* ============================================================== THE WAY BACK */

  /*
   * "Continue looking" was a fixed link to /collections/all. Someone who spent
   * ten minutes in Bokhara was dropped into a generic list at the top of the
   * page, having lost both the room they were in and their place in it.
   *
   * Two things fix that, and the second is the one that is felt:
   *
   *   1. THE BUTTON NAMES THE ROOM. It says "Back to Bokhara", not "Continue
   *      shopping" — so it is a door to somewhere rather than a way out of here.
   *   2. IT USES history.back() WHEN IT HONESTLY CAN. Only a real back
   *      restores scroll position; a fresh navigation to the same URL lands at
   *      the top and quietly loses the piece they were looking at. So it goes
   *      back only when the previous page really was that room, and navigates
   *      otherwise. Guessing wrong in either direction is worse than either.
   */
  var ROOM_KEY = 'lm-last-room';

  /* The room a visitor was in, remembered for the product page's way back:
     its address with ?room= when there is one, and its name. */
  function rememberRoom(root, roomName) {
    var title = (root.querySelector('.ls-head__title') || {}).textContent || '';
    try {
      localStorage.setItem(ROOM_KEY, JSON.stringify({
        url: location.pathname + location.search,
        title: (roomName || title).trim()
      }));
    } catch (e) {}
  }

  function Room(root) {
    /* Only a collection page has a room to remember. */
    var col = root.querySelector('[data-ls-collection]');
    if (!col) return;
    var title = (document.querySelector('.ls-head__title') || {}).textContent;
    try {
      localStorage.setItem(ROOM_KEY, JSON.stringify({
        url: location.pathname + location.search,
        title: (title || '').trim()
      }));
    } catch (e) {}
  }

  function Back(root) {
    var link = root.querySelector('[data-ls-back]');
    if (!link) return;

    var room = null;
    try { room = JSON.parse(localStorage.getItem(ROOM_KEY) || 'null'); } catch (e) {}
    if (!room || !room.url) return;

    link.setAttribute('href', room.url);
    if (room.title) {
      var label = link.querySelector('[data-ls-back-label]');
      var word = link.dataset.lsBack || 'Back to';
      if (label) label.textContent = word + ' ' + room.title;
    }

    /*
     * The referrer is the only honest signal that the previous page in THIS
     * history entry is the room. Same origin, same path — then back is a real
     * back and the scroll comes with it.
     */
    var cameFrom = false;
    try {
      if (document.referrer) {
        var r = new URL(document.referrer);
        cameFrom = r.origin === location.origin && r.pathname === room.url.split('?')[0];
      }
    } catch (e) {}

    if (!cameFrom) return;

    link.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  /* let a new tab be a new tab */
      e.preventDefault();
      history.back();
    });
  }

  /* ========================================================= THE CART, SENT */

  /*
   * The cart handed to WhatsApp as something that can be quoted against.
   *
   * The pieces are already rendered on the page, so they are read from the DOM
   * rather than fetched again — one source of truth, and no second request that
   * can disagree with what the visitor is looking at.
   */
  function cartManifest() {
    var lines = Array.prototype.slice.call(document.querySelectorAll('.ls-cl'));
    if (!lines.length) return '';

    var out = [];
    lines.forEach(function (el, i) {
      var fam = el.querySelector('.ls-cl__family');
      var name = el.querySelector('.ls-cl__name');
      var size = el.querySelector('.ls-cl__size');
      var price = el.querySelector('.ls-cl__price');
      var qty = el.querySelector('.ls-qty output');

      var bits = [];
      if (fam && fam.textContent.trim()) bits.push(fam.textContent.trim());
      if (name && name.textContent.trim()) bits.push(name.textContent.trim());
      if (size && size.textContent.trim()) bits.push(size.textContent.trim());
      if (qty && parseInt(qty.textContent, 10) > 1) bits.push(qty.textContent.trim() + ' pieces');
      if (price) bits.push(price.textContent.replace(/\s+/g, ' ').trim());

      out.push(('0' + (i + 1)).slice(-2) + ' · ' + bits.join(' · '));

      var href = name && name.getAttribute('href');
      if (href) out.push('     ' + location.origin + href);
    });

    var total = document.querySelector('.ls-sum__row--total b');
    if (total) out.push('', 'As listed: ' + total.textContent.trim());
    return out.join('\n');
  }

  /*
   * Built on click, never on load. A visitor can change a quantity or remove
   * a piece between arriving and writing, and a message composed at page load
   * would confidently describe a cart that no longer exists.
   */
  function sendCart(a) {
    var number = (a.dataset.lsWa || '').replace(/[^0-9]/g, '');
    if (!number) return false;
    var parts = [a.dataset.lsWaText || 'Hello Loomish.'];
    var man = cartManifest();
    if (man) parts.push('', man);
    var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(parts.join('\n'));
    /* Synchronously, inside the click, or the blocker eats it. */
    /* Not 'noopener' - that makes window.open return null even when the
       tab opened, and this tab then followed it to WhatsApp as well. Inside
       an app the page goes to wa.me itself - see the Desk's openOut, INSIDE
       AN APP: an app's idea of a new window can be nothing at all. */
    if (document.documentElement.classList.contains('lm-app')) { window.location.href = url; return true; }
    var win = null;
    try { win = window.open(url, '_blank'); } catch (err) {}
    if (win) { try { win.opener = null; } catch (err) {} }
    else window.location.href = url;
    return true;
  }

  function CartToWhatsApp(root) {
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-ls-wa]'));
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (sendCart(a)) e.preventDefault();
      });
    });
  }

  /*
   * THE CART PAGE'S LINE. Shopify re-renders the cart page whenever a
   * quantity changes, so its WhatsApp line is answered by one listener on the
   * document, which outlives every re-render. Until 2026-10-05 it had none at
   * all: the line was a bare wa.me link that took the visitor's tab to
   * WhatsApp without a word about the pieces they were holding.
   */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('[data-ls-cartpage] [data-ls-wa]');
    if (!a) return;
    if (sendCart(a)) e.preventDefault();
  });

  /* ============================================================== BOOT */

  function boot() {
    document.querySelectorAll('.ls[data-ls-collection]:not([data-on])').forEach(function (root) {
      root.dataset.on = '1';
      var stream = root.querySelector('.ls-stream') ? new Stream(root) : null;
      var live = stream && stream.slides ? stream : null;
      var modes = new Modes(root, live);
      new Sheets(root);
      var rooms = new Rooms(root, live, modes);
      if (!rooms.nav) rememberRoom(root, null);
      End(root, live, rooms);
      Pointer(root, live);
      Shots(root);

      /* The Theme Editor reloads a section in place; the old one lets go of
         the window before the new one takes it. */
      var sec = root.closest('.shopify-section');
      if (sec && live) {
        document.addEventListener('shopify:section:unload', function (e) {
          if (e.target === sec || (e.detail && 'shopify-section-' + e.detail.sectionId === sec.id)) live.destroy();
        });
      }
    });

    document.querySelectorAll('.ls[data-ls-product]:not([data-on])').forEach(function (root) {
      root.dataset.on = '1';
      accordions(root);
      new Gallery(root);

      /* The bar appears only when the real button has gone by. */
      Rise(root);
      commit(root);
      Room(root);
      Back(root);
      CartToWhatsApp(root);

      var bar = root.querySelector('.ls-buybar');
      var anchor = root.querySelector('[data-ls-buy-anchor]');
      if (bar && anchor && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (en) {
          bar.classList.toggle('is-on', !en[0].isIntersecting);
        }, { threshold: 0 }).observe(anchor);
      }
    });

    Cart.init();
    if (document.querySelector('[data-ls-cart]')) Cart.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
